import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import { RadioGroup, Typography } from "@mui/material";
import { ThemeWrapper } from "@verity/components";
import { CoreButton, CoreButtonRadio, CoreModal, CoreTextField, VerityLogoWithCircle } from "@verity/components/core";
import { FeedbackCategory, SatisfactionLevel } from "@verity/database/prisma";
import apiClient from "../../../helpers/api-client";
import i18n from "../../../i18n";
import styles from "./index.module.css";
import LightBulbIcon from "../../../images/portal/light-bulb.svg?react";
import BugIcon from "../../../images/portal/bug.svg?react";
import CommentIcon from "../../../images/portal/comment.svg?react";
import DotsIcon from "../../../images/portal/three-dots-circle.svg?react";

type FeedbackModalProps = {
	open: boolean;
	onClose: () => void;
};

function FeedbackModal({ open, onClose }: FeedbackModalProps) {
	const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
	const [feedbackError, setFeedbackError] = useState(false);
	const [satisfactionLevel, setSatisfactionLevel] = useState<SatisfactionLevel>();
	const [feedbackCategory, setFeedbackCategory] = useState<FeedbackCategory>();
	const [summaryInput, setSummaryInput] = useState<string | undefined>("");
	const [detailsInput, setDetailsInput] = useState<string | undefined>("");
	const satisfactionId = useId();
	const feedbackId = useId();
	const summaryId = useId();
	const detailsId = useId();
	const submitEnabled = satisfactionLevel && feedbackCategory && summaryInput && detailsInput;

	const handleCancel = () => {
		setSatisfactionLevel(undefined);
		setFeedbackCategory(undefined);
		setSummaryInput(undefined);
		setDetailsInput(undefined);
		onClose();
	};

	const submitFeedback = async () => {
		if (!satisfactionLevel || !feedbackCategory || !summaryInput || !detailsInput) return;

		try {
			await apiClient.feedback.submitFeedback({
				satisfaction: satisfactionLevel,
				category: feedbackCategory,
				summary: summaryInput,
				details: detailsInput,
			});
			setSatisfactionLevel(undefined);
			setFeedbackCategory(undefined);
			setSummaryInput(undefined);
			setDetailsInput(undefined);
		} catch (error) {
			console.error("Failed to submit feedback:", error);
			setFeedbackError(true);
		}
		setFeedbackSubmitted(true);
	};

	useEffect(() => {
		if (open) {
			setFeedbackSubmitted(false);
			setFeedbackError(false);
		}
	}, [open]);

	const satisfactionOptions = [
		{
			value: "poor",
			label: i18n.t("portal.components.feedbackButtonTSX.satisfactionLevel.poor"),
			customColor: "#c7204c",
		},
		{
			value: "satisfied",
			label: i18n.t("portal.components.feedbackButtonTSX.satisfactionLevel.satisfied"),
			customColor: "#9d6c0c",
		},
		{
			value: "good",
			label: i18n.t("portal.components.feedbackButtonTSX.satisfactionLevel.good"),
			customColor: "#0a820e",
		},
		{
			value: "excellent",
			label: i18n.t("portal.components.feedbackButtonTSX.satisfactionLevel.excellent"),
			customColor: "#053a06",
		},
	];

	const categoryOptions = [
		{
			value: "suggestion",
			label: i18n.t("portal.components.feedbackButtonTSX.feedbackCategory.suggestion"),
			startIcon: <LightBulbIcon />,
		},
		{
			value: "bug",
			label: i18n.t("portal.components.feedbackButtonTSX.feedbackCategory.bug"),
			startIcon: <BugIcon />,
		},
		{
			value: "comment",
			label: i18n.t("portal.components.feedbackButtonTSX.feedbackCategory.comment"),
			startIcon: <CommentIcon />,
		},
		{
			value: "other",
			label: i18n.t("portal.components.feedbackButtonTSX.feedbackCategory.other"),
			startIcon: <DotsIcon />,
		},
	];

	return (
		<CoreModal
			open={open}
			onClose={onClose}
			title={i18n.t("portal.components.feedbackButtonTSX.modalHeader")}
			maxWidth="600px"
			maxHeight={`calc(100% - 64px)`}
			hideHorizontalOverflow
			buttons={
				feedbackSubmitted
					? [
							{
								text: i18n.t("portal.components.feedbackButtonTSX.close"),
								color: "secondary",
								onClick: onClose,
							},
						]
					: [
							{
								text: i18n.t("portal.components.feedbackButtonTSX.cancel"),
								color: "secondary",
								onClick: handleCancel,
							},
							{
								text: i18n.t("portal.components.feedbackButtonTSX.submit"),
								color: "primary",
								disabled: !submitEnabled,
								onClick: submitFeedback,
							},
						]
			}
		>
			{feedbackSubmitted ? (
				<div className={`${styles.feedbackSubmitted}`}>
					<VerityLogoWithCircle width={125} height={125} />
					<Typography fontSize="24px" fontWeight="500">
						{feedbackError
							? i18n.t("portal.components.feedbackButtonTSX.feedbackErrorHeader")
							: i18n.t("portal.components.feedbackButtonTSX.feedbackSubmittedHeader")}
					</Typography>
					<Typography fontSize="24px" fontWeight="500">
						{feedbackError
							? i18n.t("portal.components.feedbackButtonTSX.feedbackErrorDescription")
							: i18n.t("portal.components.feedbackButtonTSX.feedbackSubmittedDescription")}
					</Typography>
				</div>
			) : (
				<div className={styles.feedbackBody}>
					<div className={styles.feedbackSection}>
						<Typography id={satisfactionId} fontSize="20px" fontWeight="500">
							{i18n.t("portal.components.feedbackButtonTSX.satisfactionHeader")}
						</Typography>
						<RadioGroup
							value={satisfactionLevel}
							className={styles.radioGroup}
							onChange={(_, value) => setSatisfactionLevel(value as SatisfactionLevel)}
							aria-labelledby={satisfactionId}
							row
						>
							{satisfactionOptions.map((option) => (
								<CoreButtonRadio
									key={option.value}
									style={{ width: "128px", height: "40px", fontSize: "14px" }}
									hoverHighlight
									disableFocusBackground
									{...option}
								/>
							))}
						</RadioGroup>
					</div>
					<div className={styles.feedbackSection}>
						<Typography id={feedbackId} fontSize="20px" fontWeight="500">
							{i18n.t("portal.components.feedbackButtonTSX.categoryHeader")}
						</Typography>
						<RadioGroup
							value={feedbackCategory}
							className={styles.radioGroup}
							onChange={(_, value) => setFeedbackCategory(value as FeedbackCategory)}
							aria-labelledby={feedbackId}
							row
						>
							{categoryOptions.map((option) => (
								<CoreButtonRadio
									key={option.value}
									style={{ width: "128px", height: "40px", fontSize: "14px" }}
									hoverHighlight
									disableFocusBackground
									{...option}
								/>
							))}
						</RadioGroup>
					</div>
					<div className={styles.feedbackSection}>
						<Typography id={summaryId} fontSize="20px" fontWeight="500">
							{i18n.t("portal.components.feedbackButtonTSX.summaryHeader")}
						</Typography>
						<CoreTextField
							required
							size="small"
							value={summaryInput}
							placeholder={i18n.t("portal.components.feedbackButtonTSX.summaryPlaceholder")}
							onChange={(e) => setSummaryInput(e.target.value)}
							sx={{ width: "100%" }}
							aria-labelledby={summaryId}
						/>
					</div>
					<div className={styles.feedbackSection}>
						<Typography id={detailsId} fontSize="20px" fontWeight="500">
							{i18n.t("portal.components.feedbackButtonTSX.detailsHeader")}
						</Typography>
						<CoreTextField
							required
							multiline
							minRows={5}
							maxRows={10}
							value={detailsInput}
							placeholder={i18n.t("portal.components.feedbackButtonTSX.detailsPlaceholder")}
							onChange={(e) => setDetailsInput(e.target.value)}
							sx={{ width: "100%" }}
							aria-labelledby={detailsId}
						/>
					</div>
				</div>
			)}
		</CoreModal>
	);
}

export default function FeedbackButton() {
	const [feedbackModalShowing, setFeedbackModalShowing] = useState(false);
	const [defaultPosition, setDefaultPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
	const containerRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const hasDragged = useRef(false);
	const handleClick = () => {
		if (!hasDragged.current) {
			setFeedbackModalShowing(true);
		}
	};

	useLayoutEffect(() => {
		if (containerRef.current) {
			setDefaultPosition({ x: 0, y: containerRef.current.offsetHeight * 0.65 });
		}
	}, []);

	return (
		<ThemeWrapper>
			<div ref={containerRef} className={styles.draggableContainer}>
				{containerRef.current && (
					<Draggable
						nodeRef={wrapperRef}
						axis="y"
						bounds="parent"
						defaultPosition={defaultPosition}
						onStart={() => {
							hasDragged.current = false;
						}}
						onDrag={(_e, data) => {
							if (Math.abs(data.deltaY) >= 2.5) {
								hasDragged.current = true;
							}
						}}
						onStop={handleClick}
					>
						<div ref={wrapperRef} className={styles.feedbackButtonWrapper}>
							<CoreButton
								color="secondary"
								className={styles.feedbackButton}
								onClick={handleClick}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										setFeedbackModalShowing(true);
									}
								}}
							>
								{i18n.t("portal.components.feedbackButtonTSX.feedback")}
							</CoreButton>
						</div>
					</Draggable>
				)}
			</div>
			<FeedbackModal open={feedbackModalShowing} onClose={() => setFeedbackModalShowing(false)} />
		</ThemeWrapper>
	);
}
